import { Inject, Injectable, OnDestroy } from '@angular/core';
import moment from 'moment-timezone';
import { forkJoin, Observable, of, Subscription } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import { Store } from '@ngrx/store';

import {
  ChartDefinitions,
  replaceAdvancedChart,
  PQEvents,
  DynamicTrendsAlarm,
} from 'src/app/_shared/services/chart-definitions.service';
import { CustomChartTypes } from 'src/app/_shared/classes/customer-chart-types.class';
import { DiligentApiService } from 'src/app/_shared/services/diligent-api.service';
import { ITrendsChart } from 'src/app/_shared/classes/chart.interface';
import { NotificationsService } from 'src/app/_shared/modules/notifications/shared/notifications.service';
import { PhaseList } from 'src/app/_shared/classes/phase-list.interface';
import { PaginatedModel } from 'src/app/_shared/interface/pagination.interface';
import {
  AlarmEventNoteResponse,
  AlarmEventNoteRequest,
} from 'src/app/_shared/interface/alarm/alarm-event-note-request.interface';
import * as fromStore from 'src/app/_store/_reducers';
import { ChartTrendData } from 'src/app/_shared/interface/chart-trend-data.interface';
import { SubscriptionData } from 'src/app/_shared/interface/subscription-data.interface';
import { NoDataPeriod } from 'src/app/_shared/interface/no-data-period.interface';
import { LockStatus } from 'src/app/_shared/interface/lock-status.interface';
import {
  ChartKeyType,
  ChartKeyConfigTypes,
} from 'src/app/_shared/services/chart-definitions/chart-config/chart-config.interface';
import { TrendAnomalyExcursionChart } from 'src/app/_shared/services/chart-definitions/charts/trend-anomaly-excursion-chart';

type TrendsResult = {
  oneminute?: ChartTrendData;
  fiveminute?: ChartTrendData;
  tenminute?: ChartTrendData;
  fifteenminute?: ChartTrendData;
};

@Injectable()
export class ChartSet implements OnDestroy {
  private name: string;
  private loadedDataStartDate: moment.Moment;
  private loadedDataEndDate: moment.Moment;
  private mCharts: ITrendsChart[];
  private chartDefinitions: ChartDefinitions;
  private dAPIService: DiligentApiService;
  private store: Store<fromStore.State>;
  private oneMinuteColumns: string[];
  private fiveMinuteColumns: string[];
  private tenMinuteColumns: string[];
  private fifteenMinuteColumns: string[];
  private loadEventsGraph = false;
  private channelsSub$: Subscription;

  public get charts(): ITrendsChart[] {
    return this.mCharts;
  }

  public set charts(newCharts: ITrendsChart[]) {
    this.mCharts = newCharts;
  }

  constructor(
    @Inject(String) name: string,
    chartKeys: Array<any>,
    chartDefinitions: ChartDefinitions,
    dAPIService: DiligentApiService,
    phaseList: Array<PhaseList>,
    notificationsService: NotificationsService,
    @Inject(String) from: string,
    store: Store<fromStore.State>
  ) {
    this.chartDefinitions = chartDefinitions;
    this.name = name;

    let i = 0;

    let excludePqAlarmForSource = ['dashboard', 'trend-anomaly-chart'];
    if (chartKeys.indexOf('pqAlarms') === -1 && !excludePqAlarmForSource.includes(from)) {
      chartKeys.splice(1, 0, 'pqAlarms');
    }
    const chartKeysLength = chartKeys.filter((x) => x !== 'pqEvents' && x !== 'notes' && x !== 'pqAlarms').length;

    this.charts = chartKeys.map((key, index) => {
      if (key === null) {
        return null;
      }
      let chartHeightMultiplier: number;
      let newChart;

      if ((newChart = this.chartDefinitions[key])) {
      } else if (key === 'pqAlarms') {
        newChart = this.chartDefinitions.getDynamicTrendAlarm();
      } else if (typeof key === 'object' && Object.hasOwn(key, 'chartKeyType')) {
        newChart = this.buildChartFromConfig(key);
      } else {
        if (from === 'dashboard') {
          newChart = this.chartDefinitions.dashboardCustom(key);
        } else {
          newChart = this.chartDefinitions.getDynamic(key);
        }
      }

      if (!(newChart instanceof PQEvents) && !(newChart instanceof DynamicTrendsAlarm)) {
        switch (chartKeysLength) {
          case 1:
            chartHeightMultiplier = 3;
            break;
          case 2:
            chartHeightMultiplier = 2;
            break;
          case 3:
            chartHeightMultiplier = 1.5;
            break;
          default:
            chartHeightMultiplier = 1;
            break;
        }

        newChart.yAxisOptions[0].height *= chartHeightMultiplier;
        if (newChart.yAxisOptions.length > 1) {
          newChart.yAxisOptions[1].height *= chartHeightMultiplier;
        }

        newChart.spacer *= chartHeightMultiplier;
      }

      newChart.yAxis = i;
      i += newChart.yAxisOptions.length;
      // replase phase for AdvancedChart
      if (phaseList) {
        phaseList.forEach((c) => {
          if (CustomChartTypes.customChartTypes[c.chart][0] === key) {
            const chartADV = replaceAdvancedChart(newChart.name, c.phase, newChart.seriesArray, c.phase);
            chartADV.replaceChannelIds(c.phase);
            chartADV.setName(c.phase);
            newChart.yAxisOptions[0].title = chartADV.yAxisOptions[0].title;
            newChart.seriesArray = chartADV.seriesArray;
          }
        });
      }
      return newChart;
    });
    this.charts = this.charts.filter((chart) => chart !== null);

    if (this.charts.indexOf(null) !== -1) {
      notificationsService.alert(
        // eslint-disable-next-line quotes
        "This Measurement Point doesn't have at least one of the channels selected in this custom tab"
      );
      this.charts = this.charts.filter((chart) => chart !== null);
    }

    this.dAPIService = dAPIService;
    this.store = store;
    this.oneMinuteColumns = this.charts
      .filter((chart) => {
        if (!this.filterChartNotEligibleForDataTable(chart)) {
          this.loadEventsGraph = true;
          return false;
        } else return true;
      })
      .map((chart) => {
        return chart.oneMinuteChannelIds;
      })
      .reduce((prev, curr) => prev.concat(curr), []);

    this.tenMinuteColumns = this.charts
      .filter((chart) => this.filterChartNotEligibleForDataTable(chart))
      .map((chart) => {
        return chart.tenMinuteChannelIds;
      })
      .reduce((prev, curr) => prev.concat(curr), []);

    this.fiveMinuteColumns = this.charts
      .filter((chart) => this.filterChartNotEligibleForDataTable(chart))
      .map((chart) => {
        return chart.fiveMinuteChannelIds;
      })
      .reduce((prev, curr) => prev.concat(curr), []);

    this.fifteenMinuteColumns = this.charts
      .filter((chart) => this.filterChartNotEligibleForDataTable(chart))
      .map((chart) => {
        return chart.fifteenMinuteChannelIds;
      })
      .reduce((prev, curr) => prev.concat(curr), []);
  }

  /**
   * Return true if the chart data is available on the minute tables (oneMinute, fiveMinute, fifteenMinute...)
   * @param chart
   * @private
   */
  private filterChartNotEligibleForDataTable(chart: any): boolean {
    return !(
      chart instanceof PQEvents ||
      chart instanceof DynamicTrendsAlarm ||
      chart === null ||
      chart instanceof TrendAnomalyExcursionChart
    );
  }

  private buildChartFromConfig(chartConfig: ChartKeyConfigTypes): any {
    if (chartConfig.chartKeyType === ChartKeyType.TREND_ANOMALY_EXCURSION) {
      return this.chartDefinitions.getTrendAnomalyExcursionChart(chartConfig);
    }
  }

  public ngOnDestroy(): void {
    this.channelsSub$.unsubscribe();
  }

  public clearChartData(): void {
    this.charts.forEach((chart) => chart.clearSeriesData());
  }

  public initializeChartSetData(
    startDate: moment.Moment,
    endDate: moment.Moment,
    granularity: string,
    interval: number,
    mpID: string,
    timezone: string,
    isInsite?: boolean,
    from?: string
  ): Observable<void[]> {
    this.loadedDataStartDate = startDate;
    this.loadedDataEndDate = endDate;
    const parsedTrendsResponse = this.fetchAndParseJSONTrendsData(
      startDate,
      endDate,
      granularity,
      interval,
      mpID,
      timezone
    );
    const noDataPeriod: Observable<Array<NoDataPeriod>> = this.dAPIService.getNoDataPeriod(mpID, startDate, endDate);
    const lockStatus: Observable<Array<LockStatus>> = this.dAPIService.getDeviceLockedStatus(mpID, startDate, endDate);

    let events: Observable<PaginatedModel<AlarmEventNoteResponse>>;
    let alarms: Observable<PaginatedModel<AlarmEventNoteResponse>>;
    if (this.loadEventsGraph) {
      const eventsRequest: AlarmEventNoteRequest = {
        measurementPointId: parseInt(mpID),
        dateRangeStart: startDate.toDate(),
        dateRangeEnd: endDate.toDate(),
        natures: ['event', 'note', 'status'],
        includeRetired: false,
        sorting: [{ column: 'triggeredWhen', desc: false }],
        offset: 0,
        count: 100000,
      };
      if (isInsite) {
        eventsRequest.severity = 1;
      }
      if (from === 'polling' || from === 'api') {
        events = this.dAPIService.getAlarmsEventsNotes(eventsRequest);
        alarms = of(null);
      } else {
        events = this.store.select((state) => state.charts.events).pipe(take(1));
        alarms = this.store.select((state) => state.charts.alarms).pipe(take(1));
      }
    }
    return forkJoin([events, parsedTrendsResponse, alarms, noDataPeriod, lockStatus]).pipe(
      map(([eventsResponse, trendsResponse, alarmsResponse, noDataPeriodResponse, lockStatusResponse]) => {
        return this.charts.map((chart) => {
          return chart.setSeriesData(
            isInsite,
            trendsResponse,
            eventsResponse?.records,
            alarmsResponse?.records,
            granularity,
            interval,
            startDate,
            endDate,
            noDataPeriodResponse,
            lockStatusResponse
          );
        });
      })
    );
  }

  private fetchAndParseJSONTrendsData(
    startDate,
    endDate,
    granularity,
    interval: number,
    mpID,
    timezone
  ): Observable<TrendsResult> {
    let tenMinInterval = interval;
    let fiveMinInterval = interval;
    let fifteenMinInterval = interval;
    if (interval === 1 && granularity === 'minute') {
      fifteenMinInterval = 15;
      tenMinInterval = 10;
      fiveMinInterval = 5;
    }

    const requests: Partial<Record<keyof TrendsResult, Observable<ChartTrendData>>> = {};

    // fifteen minute
    if (this.fifteenMinuteColumns.length > 0) {
      // interval=10 works for 1, 5, and 10 min tables (LCM=10) but not for 15 min table.
      // Use fifteenMinInterval=15 when granularity is minute.
      const intervalToUse = granularity === 'minute' ? 15 : fifteenMinInterval;

      requests.fifteenminute = this.fetchTrend(
        this.fifteenMinuteColumns,
        'fifteenminute',
        intervalToUse,
        startDate,
        endDate,
        granularity,
        mpID,
        timezone
      );
    }

    if (this.tenMinuteColumns.length > 0) {
      requests.tenminute = this.fetchTrend(
        this.tenMinuteColumns,
        'tenminute',
        tenMinInterval,
        startDate,
        endDate,
        granularity,
        mpID,
        timezone
      );
    }

    if (this.fiveMinuteColumns.length > 0) {
      requests.fiveminute = this.fetchTrend(
        this.fiveMinuteColumns,
        'fiveminute',
        fiveMinInterval,
        startDate,
        endDate,
        granularity,
        mpID,
        timezone
      );
    }

    if (this.oneMinuteColumns.length > 0) {
      requests.oneminute = this.fetchTrend(
        this.oneMinuteColumns,
        'oneminute',
        interval,
        startDate,
        endDate,
        granularity,
        mpID,
        timezone
      );
    }

    return forkJoin(requests).pipe(
      catchError((err) => {
        throw err;
      })
    );
  }

  private fetchTrend(
    columns: any[],
    type: string,
    interval: number,
    startDate: any,
    endDate: any,
    granularity: string,
    mpID: any,
    timezone: string
  ): Observable<ChartTrendData> {
    return this.dAPIService.getTrendsDataJson(startDate, endDate, granularity, columns, mpID, type, interval).pipe(
      map((response: SubscriptionData) => {
        if (response.error) {
          throw new Error(response.error);
        }

        return {
          truncatedData: response.truncatedData,
          dateStart: response.dateStart,
          trendDataTable: this.parseTrendsJSONData(response.data, timezone, granularity),
        };
      })
    );
  }

  private parseTrendsJSONData(
    channelDataJSON: any[],
    timezone: string,
    granularity: string
  ): { [id: string]: { columns: any[]; newData: any[] } } {
    const seriesObj = {};
    this.charts.forEach((chart) => {
      if (!(chart instanceof PQEvents)) {
        chart.seriesArray.forEach((series) => {
          seriesObj[series.id] = {
            columns: series.channelIds,
            newData: [],
          };
        });
      }
    });

    if (granularity === 'day') {
      channelDataJSON.forEach((entry) => {
        const dstTimestamp = moment(entry.time_stamp).format('YYYY-MM-DD');
        const timestamp = moment(dstTimestamp).tz(timezone, true).valueOf();

        for (const key in seriesObj) {
          if (seriesObj.hasOwnProperty(key)) {
            const row = seriesObj[key].columns.map((column) => parseFloat(entry[column]));
            seriesObj[key].newData.push([timestamp].concat(row));
          }
        }
      });
    } else {
      channelDataJSON.forEach((entry) => {
        const timestamp = moment(entry.time_stamp).valueOf();

        for (const key in seriesObj) {
          if (seriesObj.hasOwnProperty(key)) {
            const row = seriesObj[key].columns.map((column) => parseFloat(entry[column]));
            seriesObj[key].newData.push([timestamp].concat(row));
          }
        }
      });
    }
    return seriesObj;
  }
}
